import { type BillingPrefs } from '@/store/slices/userPreferences';
import i18n from '@/utils/i18n';

import { bankFieldsForCountry } from './bankRules';
import {
  isValidCryptoAddress,
  isValidMemo,
  memoLabelForNetwork,
  memoRequiredForNetwork,
} from './cryptoRules';

/** Per-field errors, plus `payoutMethod` for the "choose at least one" rule (not
 *  a single field). */
export type BillingErrors = Partial<Record<keyof BillingPrefs | 'payoutMethod', string>>;

const isBlank = (value: string): boolean => value.trim().length === 0;

/** Countries whose addresses carry a state/province we should capture. */
export const STATE_COUNTRIES: ReadonlySet<string> = new Set(['US', 'CA', 'AU']);

/** ISO date in the past with the beneficiary at least 18 years old. */
export const isValidAdultDob = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const dob = new Date(`${value}T00:00:00`);
  if (Number.isNaN(dob.getTime())) return false;
  const now = new Date();
  if (dob > now) return false;
  let age = now.getFullYear() - dob.getFullYear();
  const beforeBirthday =
    now.getMonth() < dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate());
  if (beforeBirthday) age -= 1;
  return age >= 18;
};

/**
 * Validate the billing/withdrawal details. Returns a per-field map of
 * natural-language error messages (empty map = valid). Covers what a real payout
 * needs: the beneficiary identity (individual or business) and KYC/tax fields
 * (always required), plus the chosen payout method(s): a routable bank account
 * for the country and/or a crypto wallet (at least one is required). The
 * bank-account fields are country-specific and come from `bankFieldsForCountry`,
 * and the crypto rules from `cryptoRules`, the same sources the form renders from,
 * so rendering and validation never drift.
 */
export const validateBilling = (b: BillingPrefs): BillingErrors => {
  const errors: BillingErrors = {};

  // Validation messages are built on demand at submit, so the i18n singleton reads
  // the current language at call time (it is not reactive UI). The rendered <Text>
  // re-runs the value through i18next harmlessly.
  const REQUIRED = i18n.t('This field is required');

  // Account type + identity (adapts to individual vs business).
  if (b.accountType === '') errors.accountType = i18n.t('Please choose individual or business');
  if (b.accountType === 'individual') {
    if (isBlank(b.accountHolderFirstName)) errors.accountHolderFirstName = REQUIRED;
    if (isBlank(b.accountHolderLastName)) errors.accountHolderLastName = REQUIRED;
  } else if (b.accountType === 'business') {
    if (isBlank(b.businessLegalName)) errors.businessLegalName = REQUIRED;
    if (b.businessType === '') errors.businessType = i18n.t('Please select a business type');
    if (isBlank(b.beneficialOwnerFirstName)) errors.beneficialOwnerFirstName = REQUIRED;
    if (isBlank(b.beneficialOwnerLastName)) errors.beneficialOwnerLastName = REQUIRED;
  }

  // Payout method: the owner must set up a bank account, a crypto wallet, or both.
  // The bank / crypto blocks below are only required when their method is enabled.
  if (!b.bankEnabled && !b.cryptoEnabled) {
    errors.payoutMethod = i18n.t('Choose at least one payout method: bank account or crypto wallet.');
  }

  // Bank account (which fields apply is country-specific; see bankRules.ts).
  if (b.bankEnabled) {
    if (isBlank(b.bankCountry)) errors.bankCountry = i18n.t('Please select the bank account country');
    if (isBlank(b.bankName)) errors.bankName = REQUIRED;
    if (isBlank(b.currency)) errors.currency = i18n.t('Please select a currency');
    bankFieldsForCountry(b.bankCountry).forEach((spec) => {
      const value = b[spec.key];
      if (spec.required && isBlank(value)) errors[spec.key] = REQUIRED;
      else if (!isBlank(value) && spec.validate && !spec.validate(value)) {
        errors[spec.key] = i18n.t('Enter a valid {{label}}', { label: i18n.t(spec.label) });
      }
    });
  }

  // Crypto wallet (network drives the asset list + the conditional memo).
  if (b.cryptoEnabled) {
    if (isBlank(b.cryptoNetwork)) errors.cryptoNetwork = i18n.t('Please select a network');
    if (isBlank(b.cryptoAsset)) errors.cryptoAsset = i18n.t('Please select a token');
    if (isBlank(b.walletAddress)) errors.walletAddress = REQUIRED;
    else if (!isValidCryptoAddress(b.cryptoNetwork, b.walletAddress)) {
      errors.walletAddress = i18n.t('Enter a valid wallet address for this network');
    }
    if (memoRequiredForNetwork(b.cryptoNetwork)) {
      const memoLabel = i18n.t(memoLabelForNetwork(b.cryptoNetwork));
      if (isBlank(b.cryptoMemo)) errors.cryptoMemo = i18n.t('{{memoLabel}} is required for this network', { memoLabel });
      else if (!isValidMemo(b.cryptoNetwork, b.cryptoMemo)) errors.cryptoMemo = i18n.t('Enter a valid {{memoLabel}}', { memoLabel });
    }
  }

  // Tax + date of birth (KYC/AML). The holder name (individual) / beneficial-owner
  // name (business) is validated in the identity block above; the DOB is the
  // individual's, or the business's beneficial owner's.
  if (isBlank(b.taxId)) errors.taxId = REQUIRED;
  if (isBlank(b.dateOfBirth)) errors.dateOfBirth = REQUIRED;
  else if (!isValidAdultDob(b.dateOfBirth)) errors.dateOfBirth = i18n.t('Enter a valid date of birth (18+)');

  // Billing address.
  if (isBlank(b.addressLine1)) errors.addressLine1 = REQUIRED;
  if (isBlank(b.city)) errors.city = REQUIRED;
  if (isBlank(b.postalCode)) errors.postalCode = REQUIRED;
  if (isBlank(b.country)) errors.country = i18n.t('Please select a country');
  if (STATE_COUNTRIES.has(b.country) && isBlank(b.stateProvince)) errors.stateProvince = REQUIRED;

  return errors;
};
